import React, { useEffect } from "react";

import { InfoSmIcon as InfoSm, Typography } from "@packages/ds";
import { useIntl } from "react-intl";

import { IconControl } from "ds/Button";
import Dialog from "ds/Dialog";
import Tag from "ds/Tag";
import useDecodedParams from "Hooks/useDecodedParams";
import { organizationByDescriptionIdSelector } from "Reducers/organization";
import {
  loadDiscounts,
  organizationActiveCommitmentsSelector
} from "Reducers/organization/discounts";
import * as O from "src/organization/pages/billing/components/Overview/BillingOverview.styles";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import * as S from "./OrganizationCommitment.style";

const formatDate = (date: string) =>
  new Date(date).toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric"
  });

const OrganizationCommitment = () => {
  const dispatch = useAppDispatch();
  const intl = useIntl();

  const { organizationId } = useDecodedParams<{
    organizationId: string;
  }>();

  const organization = useAppSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId: organizationId
    })
  );

  const discounts = useAppSelector(
    state =>
      organization &&
      organizationActiveCommitmentsSelector(state, {
        organizationId: organization.id
      })
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!discounts && organization) {
      dispatch(loadDiscounts({ organization }));
    }
  }, [discounts, dispatch, organization]);

  if (!discounts) return null;

  const Commitments = () => (
    <>
      {discounts.map(discount => (
        <O.PanelWrapper key={discount.id}>
          <O.Stack space={24}>
            <O.SpacedRow space={16}>
              <Typography.Title tag="h3">
                {intl.formatMessage(
                  {
                    id: "billing.commitments.value_per_month_commitment"
                  },
                  {
                    value: discount.commitment?.amount.monthly.formatted
                  }
                )}
              </Typography.Title>
              <Typography.BodyText $color="granite" size="small">
                {formatDate(discount.start_at)} &ndash;{" "}
                {formatDate(discount.end_at)}
              </Typography.BodyText>
            </O.SpacedRow>
            <Typography.BodyText>
              {intl.formatMessage({ id: "billing.commitments.explanation" })}
            </Typography.BodyText>
            <O.Separator type="minor" />
            <S.CommitmentBreakdownWrapper>
              <O.Stack space={32}>
                <O.Stack space={8}>
                  <O.DialogWrapper>
                    <Typography.BodyText $color="ebony" weight="bold">
                      {intl.formatMessage({
                        id: "billing.commitments.monthly"
                      })}
                    </Typography.BodyText>
                    <Dialog
                      title={intl.formatMessage({
                        id: "billing.commitments.monthly_commitment"
                      })}
                      description={intl.formatMessage({
                        id: "billing.commitments.monthly_commitment.description"
                      })}
                      trigger={
                        <IconControl aria-label="info-trigger-monthly_commitment">
                          <InfoSm />
                        </IconControl>
                      }
                    />
                  </O.DialogWrapper>
                  <S.FlexRow>
                    <Typography.BodyText>
                      {intl.formatMessage({
                        id: "billing.commitments.commitment_value"
                      })}
                    </Typography.BodyText>
                    <Typography.BodyText>
                      {discount.commitment?.amount.monthly.formatted}
                    </Typography.BodyText>
                  </S.FlexRow>
                  <S.FlexRow>
                    <Typography.BodyText>
                      {intl.formatMessage({
                        id: "billing.commitments.monthly_discount"
                      })}
                    </Typography.BodyText>
                    <Tag variant="green">
                      -{discount.discount.monthly.formatted}
                    </Tag>
                  </S.FlexRow>
                  <O.Separator type="minor" />
                  <S.FlexRow>
                    <Typography.BodyText>
                      {intl.formatMessage({
                        id: "billing.commitments.net_commitment"
                      })}
                    </Typography.BodyText>
                    <Typography.BodyText>
                      {discount.commitment?.net.monthly.formatted}
                    </Typography.BodyText>
                  </S.FlexRow>
                </O.Stack>

                <O.Stack space={8}>
                  <O.DialogWrapper>
                    <Typography.BodyText $color="ebony" weight="bold">
                      {intl.formatMessage({
                        id: "billing.commitments.contract_total"
                      })}
                    </Typography.BodyText>
                    <Dialog
                      title={intl.formatMessage({
                        id: "billing.commitments.contract_total"
                      })}
                      description={intl.formatMessage({
                        id: "billing.commitments.contract_total.description"
                      })}
                      trigger={
                        <IconControl aria-label="info-trigger-contract_total">
                          <InfoSm />
                        </IconControl>
                      }
                    />
                  </O.DialogWrapper>
                  <S.FlexRow>
                    <Typography.BodyText>
                      {intl.formatMessage({
                        id: "billing.commitments.total_commitment_value"
                      })}
                    </Typography.BodyText>
                    <Typography.BodyText>
                      {discount.commitment?.amount.contract_total.formatted}
                    </Typography.BodyText>
                  </S.FlexRow>
                  {discount.discount.contract_total && (
                    <S.FlexRow>
                      <Typography.BodyText>
                        {intl.formatMessage({
                          id: "billing.commitments.total_discount"
                        })}
                      </Typography.BodyText>
                      <Tag variant="green">
                        -{discount.discount.contract_total.formatted}
                      </Tag>
                    </S.FlexRow>
                  )}
                  <O.Separator type="minor" />
                  <S.FlexRow>
                    <Typography.BodyText>
                      {intl.formatMessage({
                        id: "billing.commitments.total_net_commitment"
                      })}
                    </Typography.BodyText>
                    <Typography.BodyText>
                      {discount.commitment?.net.contract_total.formatted}
                    </Typography.BodyText>
                  </S.FlexRow>
                </O.Stack>
              </O.Stack>
              <O.Stack space={32}>
                <O.Stack space={8}>
                  <O.DialogWrapper>
                    <Typography.BodyText $color="ebony" weight="bold">
                      {intl.formatMessage({
                        id: "billing.commitments.commitment_details"
                      })}
                    </Typography.BodyText>
                    <Dialog
                      title={intl.formatMessage({
                        id: "billing.commitments.commitment_details"
                      })}
                      description={intl.formatMessage({
                        id: "billing.commitments.commitment_details.description"
                      })}
                      trigger={
                        <IconControl>
                          <InfoSm />
                        </IconControl>
                      }
                    />
                  </O.DialogWrapper>
                  <S.FlexRow>
                    <Typography.BodyText>
                      {intl.formatMessage({
                        id: "billing.commitments.discount_type"
                      })}
                    </Typography.BodyText>
                    <S.Separator />
                    <Typography.BodyText>
                      {discount.type_label}
                    </Typography.BodyText>
                  </S.FlexRow>
                  {discount.commitment && (
                    <S.FlexRow>
                      <O.DialogWrapper>
                        <Typography.BodyText>
                          {intl.formatMessage({
                            id: "billing.commitments.commitment_period"
                          })}
                        </Typography.BodyText>
                        <Dialog
                          title={intl.formatMessage({
                            id: "billing.commitments.commitment_period"
                          })}
                          description={intl.formatMessage({
                            id: "billing.commitments.commitment_period.description"
                          })}
                          trigger={
                            <IconControl>
                              <InfoSm />
                            </IconControl>
                          }
                        />
                      </O.DialogWrapper>
                      <S.Separator />
                      <Typography.BodyText>
                        {discount.commitment.months === 1
                          ? intl.formatMessage({
                              id: "billing.commitments.monthly"
                            })
                          : intl.formatMessage(
                              {
                                id: "billing.commitments.value_months"
                              },
                              { value: discount.commitment.months }
                            )}
                      </Typography.BodyText>
                    </S.FlexRow>
                  )}
                  {discount.total_months && (
                    <S.FlexRow>
                      <Typography.BodyText>
                        {intl.formatMessage({
                          id: "billing.commitments.total_contract_duration"
                        })}
                      </Typography.BodyText>
                      <S.Separator />
                      <Typography.BodyText>
                        {intl.formatMessage(
                          {
                            id: "billing.commitments.value_months"
                          },
                          { value: discount.total_months }
                        )}
                      </Typography.BodyText>
                    </S.FlexRow>
                  )}
                  <S.FlexRow>
                    <Typography.BodyText>
                      {intl.formatMessage({
                        id: "billing.commitments.contract_starting"
                      })}
                    </Typography.BodyText>
                    <S.Separator />
                    <Typography.BodyText>
                      {formatDate(discount.start_at)}
                    </Typography.BodyText>
                  </S.FlexRow>
                  <S.FlexRow>
                    <Typography.BodyText>
                      {intl.formatMessage({
                        id: "billing.commitments.contract_ending"
                      })}
                    </Typography.BodyText>
                    <S.Separator />
                    <Typography.BodyText>
                      {formatDate(discount.end_at)}
                    </Typography.BodyText>
                  </S.FlexRow>
                </O.Stack>
              </O.Stack>
            </S.CommitmentBreakdownWrapper>
          </O.Stack>
        </O.PanelWrapper>
      ))}
    </>
  );

  return (
    <O.ContentWrapper>
      <O.InnerWrapper>
        <O.Stack>
          <Typography.Title tag="h2">
            {intl.formatMessage(
              {
                id: "billing.commitments.value_commitment"
              },
              { value: organization?.label }
            )}
          </Typography.Title>
          <Commitments />
        </O.Stack>
      </O.InnerWrapper>
    </O.ContentWrapper>
  );
};

export default OrganizationCommitment;
